import { Controller } from "@hotwired/stimulus"
import { syncReceivablesOverview } from "../utils/overview-sync.util"
import TableFrameNavigator from "../utils/table_frame_navigator"
import TableQueryState from "../utils/table_query_state"

const ARRAY_FILTER_KEYS = [
  "status[]",
  "paymentStatus[]",
  "documentType[]",
  "generatorDocumentType[]",
  "frequency[]",
  "emailDelivery[]"
]
const DERIVED_FILTER_KEYS = ["quickFilter", "hasAmountDue", "overdue"]

// Connects to data-controller="document-filter"
export default class extends Controller {
  static targets = [
    "search",
    "customer",
    "project",
    "rent",
    "paidTo",
    "paidFrom",
    "issuedFrom",
    "issuedTo",
    "dueDateFrom",
    "dueDateTo",
    "totalAmountFrom",
    "totalAmountTo",
    "deliveryDateFrom",
    "deliveryDateTo",
    "receivablesCollectedLast30",
    "statusDraft",
    "statusIssued",
    "statusSent",
    "statusActive",
    "statusAccepted",
    "statusRejected",
    "statusExpired",
    "statusCanceled",
    "statusConfirmed",
    "statusFulfilled",
    "statusDelivered",
    "statusSettled",
    "statusSuspended",
    "statusFinished",
    "paymentStatusUnpaid",
    "paymentStatusPartiallyPaid",
    "paymentStatusPaid",
    "paymentStatusOverpaid",
    "overdue",
    "documentTypeInvoice",
    "documentTypeTaxDocument",
    "documentTypeCreditNote",
    "documentTypeRentIncome",
    "generatorDocumentTypeInvoice",
    "generatorDocumentTypeProforma",
    "frequencyDaily",
    "frequencyWeekly",
    "frequencyBiweekly",
    "frequencyMonthly",
    "frequencyQuarterly",
    "frequencySemiannually",
    "frequencyYearly",
    "frequencyEvery5Minutes",
    "emailDeliveryEnabled",
    "emailDeliveryDisabled"
  ]
  static outlets = ["invoices-filter"]

  static statusesTargets = [
    "statusDraft",
    "statusIssued",
    "statusSent",
    "statusActive",
    "statusAccepted",
    "statusRejected",
    "statusExpired",
    "statusCanceled",
    "statusConfirmed",
    "statusFulfilled",
    "statusDelivered",
    "statusSettled",
    "statusSuspended",
    "statusFinished"
  ]

  static paymentStatusesTargets = [
    "paymentStatusUnpaid",
    "paymentStatusPartiallyPaid",
    "paymentStatusPaid",
    "paymentStatusOverpaid"
  ]

  static documentTypesTargets = [
    "documentTypeInvoice",
    "documentTypeTaxDocument",
    "documentTypeCreditNote",
    "documentTypeRentIncome"
  ]

  static generatorDocumentTypesTargets = ["generatorDocumentTypeInvoice", "generatorDocumentTypeProforma"]

  static frequenciesTargets = [
    "frequencyDaily",
    "frequencyWeekly",
    "frequencyBiweekly",
    "frequencyMonthly",
    "frequencyQuarterly",
    "frequencySemiannually",
    "frequencyYearly",
    "frequencyEvery5Minutes"
  ]

  static emailDeliveryTargets = ["emailDeliveryEnabled", "emailDeliveryDisabled"]

  static singleValueTargets = [
    "paidFrom",
    "paidTo",
    "issuedFrom",
    "issuedTo",
    "totalAmountFrom",
    "totalAmountTo",
    "deliveryDateFrom",
    "deliveryDateTo",
    "dueDateFrom",
    "dueDateTo",
    "customer",
    "project",
    "rent",
    "search",
    "receivablesCollectedLast30"
  ]

  initialize() {
    this.setFilterValues()
  }

  connect() {
    this.tableNavigator = new TableFrameNavigator({
      frameId: "documents",
      tableKey: this.element.closest(".documents-table-controller")?.dataset.tableNavigationTableKeyValue || null,
      onPopState: (queryState) => {
        this.setFilterValues({ queryState, includeSearch: true })
        if (this.hasInvoicesFilterOutlet) this.invoicesFilterOutlet.refreshActiveFilterIndicator()
        syncReceivablesOverview(this.application, queryState.toSearchParams())
      },
      onNavigate: ({ queryState, reason }) => {
        if (reason !== "popstate") syncReceivablesOverview(this.application, queryState.toSearchParams())
      }
    })
    this.boundNavigationRollback = this.#handleNavigationRollback.bind(this)
    document.addEventListener("lucanto:table-navigation-rollback", this.boundNavigationRollback)
  }

  disconnect() {
    document.removeEventListener("lucanto:table-navigation-rollback", this.boundNavigationRollback)
    this.tableNavigator?.disconnect()
  }

  setFilterValues({ queryState = new TableQueryState(window.location.search), includeSearch = false } = {}) {
    const urlSearchParams = queryState.toSearchParams()
    this.#resetFilterControls({ includeSearch })
    if (!urlSearchParams.has("quickFilter")) {
      const statusValues = urlSearchParams.getAll("status[]")
      this.constructor.statusesTargets.forEach((targetName) => {
        // Check if target exists before accessing it (for price offers vs invoices)
        const hasTargetProp = `has${this.#capitalizeFirstLetter(targetName)}Target`
        if (this[hasTargetProp]) {
          const target = this[`${targetName}Target`]
          if (target) target.checked = statusValues.includes(target.value)
        }
      })

      const paymentStatusValues = urlSearchParams.getAll("paymentStatus[]")
      this.constructor.paymentStatusesTargets.forEach((targetName) => {
        const hasTargetProp = `has${this.#capitalizeFirstLetter(targetName)}Target`
        if (this[hasTargetProp]) {
          const target = this[`${targetName}Target`]
          if (target) target.checked = paymentStatusValues.includes(target.value)
        }
      })

      if (this.hasOverdueTarget) this.overdueTarget.checked = urlSearchParams.has("overdue")

      const documentTypeValues = urlSearchParams.getAll("documentType[]")
      this.constructor.documentTypesTargets.forEach((targetName) => {
        const hasTargetProp = `has${this.#capitalizeFirstLetter(targetName)}Target`
        if (this[hasTargetProp]) {
          const target = this[`${targetName}Target`]
          if (target) target.checked = documentTypeValues.includes(target.value)
        }
      })

      const generatorDocumentTypeValues = urlSearchParams.getAll("generatorDocumentType[]")
      this.constructor.generatorDocumentTypesTargets.forEach((targetName) => {
        const hasTargetProp = `has${this.#capitalizeFirstLetter(targetName)}Target`
        if (this[hasTargetProp]) {
          const target = this[`${targetName}Target`]
          if (target) target.checked = generatorDocumentTypeValues.includes(target.value)
        }
      })

      const frequencyValues = urlSearchParams.getAll("frequency[]")
      this.constructor.frequenciesTargets.forEach((targetName) => {
        const hasTargetProp = `has${this.#capitalizeFirstLetter(targetName)}Target`
        if (this[hasTargetProp]) {
          const target = this[`${targetName}Target`]
          if (target) target.checked = frequencyValues.includes(target.value)
        }
      })

      const emailDeliveryValues = urlSearchParams.getAll("emailDelivery[]")
      this.constructor.emailDeliveryTargets.forEach((targetName) => {
        const hasTargetProp = `has${this.#capitalizeFirstLetter(targetName)}Target`
        if (this[hasTargetProp]) {
          const target = this[`${targetName}Target`]
          if (target) target.checked = emailDeliveryValues.includes(target.value)
        }
      })

      this.constructor.singleValueTargets.forEach((targetName) => {
        // we have data-turbo-pernament=true on search so we need to skip this for search
        if (targetName === "search" && !includeSearch) {
          return
        }
        // Check if target exists before accessing it (for price offers vs invoices)
        const hasTargetProp = `has${this.#capitalizeFirstLetter(targetName)}Target`
        if (this[hasTargetProp]) {
          const target = this[`${targetName}Target`]
          if (target) target.value = urlSearchParams.get(targetName) || ""
        }
      })
    }
  }

  activeStatusChecker(event) {
    const invoicesFilterController = this.invoicesFilterOutlet
    const mapping = {
      generatorDocumentType: [
        this.constructor.generatorDocumentTypesTargets
          .map((target) => `${target}Target`)
          .filter((t) => {
            const targetName = t.replace("Target", "")
            const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
            return this[hasTargetProperty]
          }),
        invoicesFilterController.hasActiveStatusRecurringDocumentTypeTarget
          ? invoicesFilterController.activeStatusRecurringDocumentTypeTarget
          : null
      ],
      frequency: [
        this.constructor.frequenciesTargets
          .map((target) => `${target}Target`)
          .filter((t) => {
            const targetName = t.replace("Target", "")
            const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
            return this[hasTargetProperty]
          }),
        invoicesFilterController.hasActiveStatusFrequencyTarget
          ? invoicesFilterController.activeStatusFrequencyTarget
          : null
      ],
      emailDelivery: [
        this.constructor.emailDeliveryTargets
          .map((target) => `${target}Target`)
          .filter((t) => {
            const targetName = t.replace("Target", "")
            const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
            return this[hasTargetProperty]
          }),
        invoicesFilterController.hasActiveStatusEmailDeliveryTarget
          ? invoicesFilterController.activeStatusEmailDeliveryTarget
          : null
      ],
      documentType: [
        this.constructor.documentTypesTargets
          .map((target) => `${target}Target`)
          .filter((t) => {
            const targetName = t.replace("Target", "")
            const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
            return this[hasTargetProperty]
          }),
        invoicesFilterController.hasActiveStatusDocumentTypeTarget
          ? invoicesFilterController.activeStatusDocumentTypeTarget
          : null
      ],
      status: [
        this.constructor.statusesTargets
          .map((target) => `${target}Target`)
          .filter((t) => {
            const targetName = t.replace("Target", "")
            const hasTargetProperty = `has${this.#capitalizeFirstLetter(targetName)}Target`
            return this[hasTargetProperty]
          }),
        invoicesFilterController.activeStatusStateTarget
      ],
      paymentStatus: [
        this.#paymentFilterTargetNames(),
        invoicesFilterController.activeStatusStateTarget
      ],
      overdue: [this.#paymentFilterTargetNames(), invoicesFilterController.activeStatusStateTarget],
      customer: [
        ["customerTarget"],
        invoicesFilterController.hasActiveStatusCustomerTarget
          ? invoicesFilterController.activeStatusCustomerTarget
          : null
      ],
      project: [
        ["projectTarget"],
        invoicesFilterController.hasActiveStatusProjectTarget
          ? invoicesFilterController.activeStatusProjectTarget
          : null
      ],
      rent: [
        ["rentTarget"],
        invoicesFilterController.hasActiveStatusRentTarget ? invoicesFilterController.activeStatusRentTarget : null
      ],
      Amount: [
        ["totalAmountFromTarget", "totalAmountToTarget"],
        invoicesFilterController.hasActiveStatusTotalAmountTarget
          ? invoicesFilterController.activeStatusTotalAmountTarget
          : null
      ],
      issued: [
        ["issuedFromTarget", "issuedToTarget"],
        invoicesFilterController.hasActiveStatusExposedTarget
          ? invoicesFilterController.activeStatusExposedTarget
          : null
      ],
      dueDate: [
        ["dueDateFromTarget", "dueDateToTarget"],
        this.hasDueDateFromTarget && invoicesFilterController.hasActiveStatusDueDateTarget
          ? invoicesFilterController.activeStatusDueDateTarget
          : null
      ],
      deliveryDate: [
        ["deliveryDateFromTarget", "deliveryDateToTarget"],
        this.hasDeliveryDateFromTarget && invoicesFilterController.hasActiveStatusDeliveryDateTarget
          ? invoicesFilterController.activeStatusDeliveryDateTarget
          : null
      ],
      paid: [
        ["paidFromTarget", "paidToTarget"],
        this.hasPaidFromTarget && invoicesFilterController.hasActiveStatusPaymentDateTarget
          ? invoicesFilterController.activeStatusPaymentDateTarget
          : null
      ]
    }

    for (const key in mapping) {
      if (event.target.name.includes(key)) {
        const [targets, statusTarget] = mapping[key]
        if (statusTarget) {
          this.#statusChecker(invoicesFilterController, targets.flat(), statusTarget)
        }
        break
      }
    }
  }

  cancelFilters() {
    this.removeValuesFromFilters()
    this.#constructUrlAndSendRequest()
  }

  removeValuesFromFilters() {
    this.constructor.targets.forEach((targetName) => {
      // Check if target exists before accessing it (for price offers vs invoices)
      if (this.#hasTarget(targetName)) {
        const target = this[`${targetName}Target`]
        if (target.type === "checkbox" || target.type === "radio") {
          target.checked = false
        } else {
          target.value = ""
        }
      }
    })
  }

  cancelChipFilter(event) {
    const target = this[`${event.currentTarget.dataset.target}Target`]
    if (target) {
      if (target.type === "checkbox") {
        target.checked = false
      } else {
        target.value = ""
      }
      this.#constructUrlAndSendRequest()
    }
  }

  applyFilters(event) {
    this.#constructUrlAndSendRequest({ search: event?.target?.name === "search" })
  }

  #constructUrlAndSendRequest({ search = false } = {}) {
    const queryState = this.#constructQueryState()
    if (search) {
      // Deliberately NO restoreFocusSelector, unlike contacts: this list's search
      // field is `data-turbo-permanent`, so Turbo MOVES the same node into the
      // replacement frame (FrameRenderer#render → preservingPermanentElements)
      // and its Bardo re-focuses whatever held focus inside it. Naming a restore
      // target on top of that re-runs table_frame_navigator's
      // setSelectionRange(end, end) up to three times per render, which yanks the
      // caret to the end of the field mid-word. Contacts needs the option because
      // its field is NOT permanent and is genuinely rebuilt.
      this.tableNavigator.scheduleSearch(queryState, { preserveScroll: { x: true, y: false } })
    } else {
      this.tableNavigator.navigate(queryState, {
        history: "push",
        reason: "filter",
        preserveScroll: { x: true, y: false }
      })
    }
  }

  #statusChecker(invoicesFilterController, targets, statusTarget) {
    const status = targets.some((target) => {
      if (!this[`has${this.#capitalizeFirstLetter(target)}`]) {
        return false
      }
      const element = this[target]
      if (element.type === "checkbox") {
        return element?.checked
      } else {
        return element.value
      }
    })
    invoicesFilterController.toggleActiveStatus(status, statusTarget)
  }

  #constructQueryState() {
    const changes = Object.fromEntries(
      [...ARRAY_FILTER_KEYS, ...this.constructor.singleValueTargets, ...DERIVED_FILTER_KEYS].map((key) => [key, null])
    )
    const statuses = this.#getStatusParams()
    const paymentStatuses = this.#getCheckedValues(this.constructor.paymentStatusesTargets)
    const documentTypes = this.#getDocumentTypeParams()
    const generatorDocumentTypes = this.#getCheckedValues(this.constructor.generatorDocumentTypesTargets)
    const frequencies = this.#getCheckedValues(this.constructor.frequenciesTargets)
    const emailDelivery = this.#getCheckedValues(this.constructor.emailDeliveryTargets)
    this.#setSingleValueChanges(changes)
    changes["status[]"] = statuses
    changes["paymentStatus[]"] = paymentStatuses
    changes.overdue = this.hasOverdueTarget && this.overdueTarget.checked ? "1" : null
    changes["documentType[]"] = documentTypes
    changes["generatorDocumentType[]"] = generatorDocumentTypes
    changes["frequency[]"] = frequencies
    changes["emailDelivery[]"] = emailDelivery
    return new TableQueryState(window.location.search).patch(changes)
  }

  #getStatusParams() {
    const array = []
    this.constructor.statusesTargets.forEach((targetName) => {
      // Check if target exists before accessing it (for price offers vs invoices)
      if (this.#hasTarget(targetName)) {
        const target = this[`${targetName}Target`]
        if (target?.checked && target?.value !== "") {
          array.push(target.value)
        }
      }
    })

    return array
  }

  #getDocumentTypeParams() {
    const array = []
    this.constructor.documentTypesTargets.forEach((targetName) => {
      if (this.#hasTarget(targetName)) {
        const target = this[`${targetName}Target`]
        if (target?.checked && target?.value !== "") {
          array.push(target.value)
        }
      }
    })
    return array
  }

  #paymentFilterTargetNames() {
    const targets = this.constructor.paymentStatusesTargets
      .map((target) => `${target}Target`)
      .filter((target) => this.#hasTarget(target.replace("Target", "")))
    if (this.hasOverdueTarget) targets.push("overdueTarget")
    return targets
  }

  #setSingleValueChanges(changes) {
    this.constructor.singleValueTargets.forEach((targetName) => {
      // Check if target exists before accessing it (for price offers vs invoices)
      if (this.#hasTarget(targetName)) {
        const target = this[`${targetName}Target`]
        if (target?.value && target?.value !== "") {
          changes[targetName] = target.value
        }
      }
    })
  }

  #getCheckedValues(targetNames) {
    const array = []
    targetNames.forEach((targetName) => {
      if (this.#hasTarget(targetName)) {
        const target = this[`${targetName}Target`]
        if (target?.checked && target?.value !== "") {
          array.push(target.value)
        }
      }
    })
    return array
  }

  #resetFilterControls({ includeSearch }) {
    this.constructor.targets.forEach((targetName) => {
      if (targetName === "search" && !includeSearch) return
      if (!this.#hasTarget(targetName)) return

      const target = this[`${targetName}Target`]
      if (target.type === "checkbox" || target.type === "radio") {
        target.checked = false
      } else {
        target.value = ""
      }
    })
  }

  #capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  #hasTarget(targetName) {
    const hasTargetProp = `has${this.#capitalizeFirstLetter(targetName)}Target`
    return this[hasTargetProp]
  }

  #handleNavigationRollback(event) {
    if (event.detail?.frameId !== "documents") return
    const tableKey = this.element.closest(".documents-table-controller")?.dataset.tableNavigationTableKeyValue || null
    if (event.detail?.tableKey && event.detail.tableKey !== tableKey) return
    this.setFilterValues({ queryState: new TableQueryState(window.location.search), includeSearch: true })
    if (this.hasInvoicesFilterOutlet) this.invoicesFilterOutlet.refreshActiveFilterIndicator()
    syncReceivablesOverview(this.application, new URLSearchParams(window.location.search))
  }
}
